import React, { useEffect, useState } from 'react'
import { Warning, UserCircle } from '@phosphor-icons/react'
import type { WorkflowNodeData } from '@/types/workflow'
import { workflowsApi, type AssignableMember } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'

interface AssignOwnerConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
  /** Whether the workflow contains a Create Task node. Assign Owner only
   *  stamps an owner onto tasks, so with no Create Task node it does nothing.
   *  Defaults to true so the warning never shows when the flag isn't supplied. */
  hasCreateTaskNode?: boolean
}

import { inputClass, inputStyle, sectionClass, infoBannerClass, warnBannerClass, pillTextStyle } from './_kit'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
// 'weighted' was removed (AB#21985) - the executor (campaign_executor.py's
// _handle_assign_owner) never read assignment_mode at all, so selecting it
// silently did the exact same thing as Round Robin. Only Round Robin is
// actually implemented, which is why this is no longer a dropdown: a select
// control with a single, unchangeable option is confusing rather than
// informative (reporter follow-up on AB#21985: "Is drop down necessary
// here? Nothing to choose."). The value is still sent to the backend as
// `assignment_mode: 'round_robin'` below so campaign_executor.py's data
// contract is unchanged - only the picker UI is gone.

const DEFAULT_CONFIG = {
  team_member_emails: [] as string[],
  assignment_mode: 'round_robin',
}

export default function AssignOwnerConfig({ data, updateField, hasCreateTaskNode = true }: AssignOwnerConfigProps) {
  const config = (data.assign_owner_config || DEFAULT_CONFIG) as {
    team_member_emails: string[]
    assignment_mode: string
  }
  const currentUser = useAuthStore((s) => s.user)

  const [newEmail, setNewEmail] = useState('')
  const [members, setMembers] = useState<AssignableMember[]>([])
  const [dropdownEligible, setDropdownEligible] = useState(false)
  const [planName, setPlanName] = useState<string | null>(null)
  const [loadingMembers, setLoadingMembers] = useState(true)
  const [pickerValue, setPickerValue] = useState('')

  // The account's plan decides the picker's shape (see PR description in
  // AssignOwnerConfig's design note below), so it's fetched once per mount
  // rather than threaded through as a prop from every node-config caller.
  useEffect(() => {
    let cancelled = false
    workflowsApi
      .assignableMembers()
      .then((res) => {
        if (cancelled) return
        setMembers(res.data.members)
        setDropdownEligible(res.data.dropdown_eligible)
        setPlanName(res.data.plan_name)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingMembers(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const updateConfig = (updates: Partial<typeof config>) => {
    updateField('assign_owner_config', { ...config, ...updates })
  }

  const addEmail = (email: string) => {
    const trimmed = email.trim()
    if (!trimmed) return
    if (config.team_member_emails.includes(trimmed)) return
    updateConfig({ team_member_emails: [...config.team_member_emails, trimmed] })
  }

  const removeEmail = (email: string) => {
    updateConfig({
      team_member_emails: config.team_member_emails.filter((e) => e !== email),
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addEmail(newEmail)
      setNewEmail('')
    }
  }

  // Members not already added — the picker only offers people not yet on
  // the list, so there's no way to "add" a duplicate through it.
  const availableMembers = members.filter((m) => !config.team_member_emails.includes(m.email))
  const memberByEmail = new Map(members.map((m) => [m.email, m]))
  const labelFor = (email: string) => memberByEmail.get(email)?.full_name || email

  return (
    <div className={sectionClass}>
      {!hasCreateTaskNode && (
        <div className={warnBannerClass}>
          <Warning className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" />
          <p className="text-xs">
            This node has no effect without a <span className="font-medium">Create Task</span> node
            in the workflow. Add a Create Task node so there are tasks to assign an owner to.
          </p>
        </div>
      )}

      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            className={inputClass} style={inputStyle}
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="Assign Owner"
          />
        )}
      </Field>

      <Field
        label="Assignment Mode"
        as="group"
        hint="Round Robin assigns owners in a fixed rotation across the team members below (the same contact always maps to the same owner, so re-running the workflow never reassigns them). It is the only assignment mode available, so there is nothing to choose here."
      >
        <span
          className="inline-flex items-center h-7 px-2.5 rounded-full border bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30"
          style={pillTextStyle}
        >
          Round Robin
        </span>
      </Field>

      {loadingMembers ? null : dropdownEligible ? (
        <Field
          label="Team Members"
          hint="Pick from your account's team — only real teammates can be assigned, so contacts never end up owned by a typo."
        >
          {(f) => (
            <>
              <div className="flex gap-2">
                <Select
                  {...f}
                  value={pickerValue}
                  onChange={(email) => {
                    addEmail(email)
                    setPickerValue('')
                  }}
                  options={availableMembers.map((m) => ({
                    value: m.email,
                    label: m.full_name,
                    description: m.email,
                  }))}
                  placeholder={availableMembers.length ? 'Choose a teammate…' : 'Everyone is already added'}
                  disabled={availableMembers.length === 0}
                  className="flex-1"
                  buttonClassName="w-full"
                />
              </div>
              {config.team_member_emails.length > 0 && (
                <div className="mt-2 space-y-1">
                  {config.team_member_emails.map((email) => (
                    <div
                      key={email}
                      className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-lg"
                    >
                      <span className="text-sm text-foreground truncate">{labelFor(email)}</span>
                      <button
                        onClick={() => removeEmail(email)}
                        className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-500/15 rounded transition-all duration-150 flex-shrink-0 ml-2"
                        aria-label={`Remove ${labelFor(email)}`}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Field>
      ) : (
        <Field
          label="Team Member Emails"
          hint={
            planName
              ? `Upgrade to SMB or Enterprise to pick teammates from a list instead of typing emails.`
              : undefined
          }
        >
          {(f) => (
            <>
              <div className="flex gap-2">
                <input
                  {...f}
                  type="text"
                  className={inputClass} style={inputStyle}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="team@example.com"
                />
                <button
                  onClick={() => { addEmail(newEmail); setNewEmail('') }}
                  className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90 transition-all duration-150 flex-shrink-0"
                >
                  Add
                </button>
              </div>

              {currentUser?.email && !config.team_member_emails.includes(currentUser.email) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => addEmail(currentUser.email)}
                  icon={<UserCircle className="w-3.5 h-3.5" />}
                >
                  Assign to me
                </Button>
              )}

              {config.team_member_emails.length > 0 && (
                <div className="mt-2 space-y-1">
                  {config.team_member_emails.map((email) => (
                    <div
                      key={email}
                      className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-lg"
                    >
                      <span className="text-sm text-foreground truncate">
                        {email}
                      </span>
                      <button
                        onClick={() => removeEmail(email)}
                        className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-500/15 rounded transition-all duration-150 flex-shrink-0 ml-2"
                        aria-label={`Remove ${email}`}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Field>
      )}

      <div className={infoBannerClass}>
        <p className="text-xs">
          Distributes contacts across team members. The assigned owner is
          carried onto any tasks created for that contact in this workflow.
        </p>
      </div>
    </div>
  )
}
